/**
 * Insole V2 Pipeline API
 * 
 * API functions for the V2 insole generation pipeline including alignment,
 * template management, and manual adjustments.
 */

import { logAPIError } from '../../utils/errorLogger'
import type {
  TemplateListResponse,
  TemplateTraceResponse,
  AlignRequest,
  AlignResponse,
  AlignAdjustRequest,
  AlignAdjustResponse,
  DrapeRequest,
  DrapeResponse,
  BoundaryAdjustRequest,
  BoundaryAdjustResponse,
  BoundarySmoothRequest,
  BoundarySmoothResponse,
  BoundaryApproveRequest,
  BoundaryApproveResponse,
  GenerateRequest,
  GenerateResponse,
  ExportFormat,
  ExportResponse,
  ApproveRequest,
  ApproveResponse,
  ValidateResponse
} from '../types'

/**
 * Type signature for the makeRequest method from BackendService
 */
export type MakeRequest = <T>(endpoint: string, options?: RequestInit, timeoutMs?: number | null) => Promise<T>

/**
 * Extract the structured `detail` object from a backend error, if present.
 * FastAPI returns HTTPException detail as either a string or a
 * `{error, message, ...}` object; this helper normalises both.
 */
export function getErrorDetail(err: unknown): Record<string, any> | null {
  const detail = (err as any)?.data?.detail
  if (!detail || typeof detail !== 'object') return null
  return detail as Record<string, any>
}

/**
 * True when the backend rejected an align/drape call because the scan lacks
 * a 3-point calibration (heel/MT1/MT5). Backend PR #414/#415 removed the PCA
 * fallback, so this is now a hard-stop.
 */
export function isCalibrationRequiredError(err: unknown): boolean {
  return getErrorDetail(err)?.error === 'calibration_required'
}

/**
 * Turn a backend alignment failure into a user-facing message with an
 * actionable next step ("try a bigger/smaller template", etc.). Falls back to
 * the error's own message when the structured detail isn't recognized.
 *
 * Backend error codes (see routes/insole_v2_routes.py):
 *   - calibration_required             → scan missing heel/MT1/MT5 calibration
 *   - template_foot_size_mismatch      → EU gap too wide
 *   - alignment_residual_too_high      → shape doesn't fit (ICP residual)
 *   - alignment_length_ratio_out_of_range → template too long/short vs footprint
 */
export function formatAlignError(err: unknown): string {
  const base = err instanceof Error ? err.message : 'Alignment failed'
  const detail = getErrorDetail(err)
  if (!detail) return base

  switch (detail.error) {
    case 'calibration_required':
      return (
        'This scan is not calibrated. The V2 pipeline needs the three ' +
        'anatomical landmarks (heel, MT1, MT5) before it can align a ' +
        'template. Open the STL Input view and use "Calibrate Foot" to ' +
        'place them, then retry.'
      )
    case 'template_foot_size_mismatch': {
      const templateEu = Number(detail.template_eu)
      const footEu = Number(detail.foot_eu)
      if (Number.isFinite(templateEu) && Number.isFinite(footEu)) {
        const direction = templateEu < footEu ? 'bigger' : 'smaller'
        return `${base} Try a ${direction} template (around EU ${Math.round(footEu)}).`
      }
      return base
    }
    case 'alignment_length_ratio_out_of_range': {
      const ratio = Number(detail.length_ratio)
      if (Number.isFinite(ratio)) {
        const direction = ratio > 1 ? 'smaller' : 'bigger'
        return `${base} Try a ${direction} template — the current one is ${
          ratio > 1 ? 'longer' : 'shorter'
        } than the foot.`
      }
      return base
    }
    case 'alignment_residual_too_high':
      return `${base} Try a different template — the shape does not match the foot well enough.`
    default:
      return base
  }
}

/**
 * List available insole templates
 * 
 * @param makeRequest - The makeRequest method from BackendService
 * @param page - Page number (1-indexed)
 * @param pageSize - Number of templates per page
 * @returns List of available templates
 */
export async function listTemplates(
  makeRequest: MakeRequest,
  page: number = 1,
  pageSize: number = 50
): Promise<TemplateListResponse> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString()
    })
    
    const response = await makeRequest<TemplateListResponse>(
      `/api/v2/templates?${params}`,
      { method: 'GET' }
    )
    
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to list templates'
    logAPIError('/api/v2/templates', 'GET', 0, errorMessage)
    throw error
  }
}

/**
 * Perform initial alignment of STL with template
 * 
 * @param makeRequest - The makeRequest method from BackendService
 * @param request - Alignment request with STL file ID and template ID
 * @returns Alignment response with session ID and aligned template points
 */
export async function alignInsole(
  makeRequest: MakeRequest,
  request: AlignRequest
): Promise<AlignResponse> {
  try {
    const response = await makeRequest<AlignResponse>(
      '/api/v2/insole/align',
      {
        method: 'POST',
        body: JSON.stringify(request)
      },
      null
    )
    
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to align insole'
    logAPIError('/api/v2/insole/align', 'POST', 0, errorMessage, request)
    throw error
  }
}

/**
 * Adjust alignment manually (translate, rotate, scale)
 * 
 * @param makeRequest - The makeRequest method from BackendService
 * @param request - Adjustment request with session ID and transform parameters
 * @returns Updated alignment with new template points
 */
export async function adjustAlignment(
  makeRequest: MakeRequest,
  request: AlignAdjustRequest
): Promise<AlignAdjustResponse> {
  try {
    const response = await makeRequest<AlignAdjustResponse>(
      '/api/v2/insole/align/adjust',
      {
        method: 'PUT',
        body: JSON.stringify(request)
      }
    )
    
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to adjust alignment'
    logAPIError('/api/v2/insole/align/adjust', 'PUT', 0, errorMessage, request)
    throw error
  }
}

/**
 * Drape aligned template onto 3D foot scan
 * 
 * @param makeRequest - The makeRequest method from BackendService
 * @param request - Drape request with session ID, template, and STL
 * @returns Drape response with 3D boundary curve and quality metrics
 */
export async function drapeTemplate(
  makeRequest: MakeRequest,
  request: DrapeRequest
): Promise<DrapeResponse> {
  try {
    const response = await makeRequest<DrapeResponse>(
      '/api/v2/insole/drape',
      {
        method: 'POST',
        body: JSON.stringify(request)
      },
      null
    )
    
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to drape template'
    logAPIError('/api/v2/insole/drape', 'POST', 0, errorMessage, request)
    throw error
  }
}

/**
 * Adjust 3D boundary curve control points
 * 
 * @param makeRequest - The makeRequest method from BackendService
 * @param request - Boundary adjustment request with control points
 * @returns Updated boundary information
 */
export async function adjustBoundary(
  makeRequest: MakeRequest,
  request: BoundaryAdjustRequest
): Promise<BoundaryAdjustResponse> {
  try {
    const response = await makeRequest<BoundaryAdjustResponse>(
      '/api/v2/insole/boundary/adjust',
      {
        method: 'PUT',
        body: JSON.stringify(request)
      }
    )
    
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to adjust boundary'
    logAPIError('/api/v2/insole/boundary/adjust', 'PUT', 0, errorMessage, request)
    throw error
  }
}

/**
 * Smooth a region of the boundary curve
 */
export async function smoothBoundary(
  makeRequest: MakeRequest,
  request: BoundarySmoothRequest
): Promise<BoundarySmoothResponse> {
  try {
    const response = await makeRequest<BoundarySmoothResponse>(
      '/api/v2/insole/boundary/smooth',
      {
        method: 'POST',
        body: JSON.stringify(request)
      }
    )
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to smooth boundary'
    logAPIError('/api/v2/insole/boundary/smooth', 'POST', 0, errorMessage, request)
    throw error
  }
}

/**
 * Approve current boundary for generation
 */
export async function approveBoundary(
  makeRequest: MakeRequest,
  request: BoundaryApproveRequest
): Promise<BoundaryApproveResponse> {
  try {
    const response = await makeRequest<BoundaryApproveResponse>(
      '/api/v2/insole/boundary/approve',
      {
        method: 'POST',
        body: JSON.stringify(request)
      }
    )
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to approve boundary'
    logAPIError('/api/v2/insole/boundary/approve', 'POST', 0, errorMessage, request)
    throw error
  }
}

/**
 * Generate insole mesh from approved boundary
 */
export async function generateInsoleV2(
  makeRequest: MakeRequest,
  request: GenerateRequest
): Promise<GenerateResponse> {
  try {
    const response = await makeRequest<GenerateResponse>(
      '/api/v2/insole/generate',
      {
        method: 'POST',
        body: JSON.stringify(request)
      },
      null
    )
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to generate insole'
    logAPIError('/api/v2/insole/generate', 'POST', 0, errorMessage, request)
    throw error
  }
}

/**
 * Export insole mesh in specified format
 */
export async function exportInsole(
  makeRequest: MakeRequest,
  meshId: string,
  format: ExportFormat
): Promise<ExportResponse> {
  try {
    const params = new URLSearchParams({ format })
    const response = await makeRequest<ExportResponse>(
      `/api/v2/insole/${meshId}/export?${params}`,
      { method: 'GET' }
    )
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to export insole'
    logAPIError(`/api/v2/insole/${meshId}/export`, 'GET', 0, errorMessage)
    throw error
  }
}

/**
 * Doctor approval with HIPAA sign-off
 */
export async function approveInsole(
  makeRequest: MakeRequest,
  meshId: string,
  request: ApproveRequest
): Promise<ApproveResponse> {
  try {
    const response = await makeRequest<ApproveResponse>(
      `/api/v2/insole/${meshId}/approve`,
      {
        method: 'POST',
        body: JSON.stringify(request)
      }
    )
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to approve insole'
    logAPIError(`/api/v2/insole/${meshId}/approve`, 'POST', 0, errorMessage, request)
    throw error
  }
}

/**
 * Validate insole mesh quality
 */
export async function validateInsole(
  makeRequest: MakeRequest,
  meshId: string
): Promise<ValidateResponse> {
  try {
    const response = await makeRequest<ValidateResponse>(
      `/api/v2/insole/${meshId}/validate`,
      { method: 'GET' }
    )
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to validate insole'
    logAPIError(`/api/v2/insole/${meshId}/validate`, 'GET', 0, errorMessage)
    throw error
  }
}

/**
 * Trace a scanned template image into outline points and save to DB
 */
export async function traceTemplate(
  baseUrl: string,
  authToken: string,
  params: {
    file: File
    size_eu: number
    gender: string
    type: string
    name: string
    is_shared: boolean
    save: boolean
  }
): Promise<TemplateTraceResponse> {
  const formData = new FormData()
  formData.append('file', params.file)
  formData.append('size_eu', params.size_eu.toString())
  formData.append('gender', params.gender)
  formData.append('type', params.type)
  formData.append('name', params.name)
  formData.append('is_shared', params.is_shared.toString())
  formData.append('save', params.save.toString())

  const response = await fetch(`${baseUrl}/api/v2/templates/trace`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
    body: formData,
    credentials: 'include',
  })

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: response.statusText }))
    throw new Error(err.detail || `Upload failed: ${response.status}`)
  }

  return response.json()
}

/**
 * Delete a template by ID
 */
export async function deleteTemplate(
  makeRequest: MakeRequest,
  templateId: string
): Promise<{ deleted: boolean }> {
  try {
    const response = await makeRequest<{ deleted: boolean }>(
      `/api/v2/templates/${templateId}`,
      { method: 'DELETE' }
    )
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to delete template'
    logAPIError(`/api/v2/templates/${templateId}`, 'DELETE', 0, errorMessage)
    throw error
  }
}

/**
 * Hard-cancel an in-flight V2 pipeline (alignment / draping / generation).
 * Flips a server-side cancellation event so cooperative checks bail out at
 * the next stage boundary, and marks the session status as 'canceled'.
 */
export async function cancelV2Session(
  makeRequest: MakeRequest,
  sessionId: string
): Promise<{ session_id: string; previous_status: string; canceled_in_flight: boolean }> {
  try {
    return await makeRequest(
      `/api/v2/insole/cancel/${sessionId}`,
      { method: 'POST' }
    )
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to cancel V2 session'
    logAPIError(`/api/v2/insole/cancel/${sessionId}`, 'POST', 0, errorMessage)
    throw error
  }
}

/**
 * Hard-cancel an in-flight V1 (Classic) insole generation for *projectId*.
 */
export async function cancelClassicInsole(
  makeRequest: MakeRequest,
  projectId: string
): Promise<{ project_id: string; canceled_in_flight: boolean }> {
  try {
    return await makeRequest(
      `/api/projects/${projectId}/insole/cancel`,
      { method: 'POST' }
    )
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to cancel classic generation'
    logAPIError(`/api/projects/${projectId}/insole/cancel`, 'POST', 0, errorMessage)
    throw error
  }
}
